import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Card,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  HomeOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { getCampusList, createCampus, updateCampus, deleteCampus } from '../../services/campus';
import { Campus, PaginatedResponse } from '../../types';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import '../../styles/admin-pages.css';
import './CampusManagement.css';

const { Option } = Select;

const CampusManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<Campus> | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampus, setEditingCampus] = useState<Campus | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { isMobile } = useBreakpoint();

  const fetchData = async (params?: any) => {
    setLoading(true);
    try {
      const res = await getCampusList({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
      });
      setData(res);
    } catch (error) {
      message.error('获取校区列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize]);

  const handleAdd = () => {
    setEditingCampus(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Campus) => {
    setEditingCampus(record);
    form.setFieldsValue({
      ...record,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCampus(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCampus) {
        await updateCampus(editingCampus.id, values);
        message.success('更新成功');
      } else {
        await createCampus(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(editingCampus ? '更新失败' : '创建失败');
    }
  };

  const columns = [
    {
      title: '校区编码',
      dataIndex: 'campusCode',
      key: 'campusCode',
      render: (code: string) => (
        <span className="campus-code">{code}</span>
      ),
    },
    {
      title: '校区名称',
      dataIndex: 'campusName',
      key: 'campusName',
      render: (name: string) => (
        <div className="campus-name">
          <HomeOutlined className="campus-name-icon" />
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      render: (name: string) => (
        <div className="campus-contact">
          <UserOutlined className="campus-contact-icon" />
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      render: (phone: string) => (
        <div className="campus-phone">
          <PhoneOutlined className="campus-phone-icon" />
          <span>{phone}</span>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag className={`admin-tag ${status === 'enabled' ? 'admin-tag-success' : 'admin-tag-error'}`}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_: any, record: Campus) => (
        <Space size="small">
          <Tooltip title="编辑">
            <button
              className="admin-action-btn admin-action-btn-edit"
              onClick={() => handleEdit(record)}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定删除该校区吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <button className="admin-action-btn admin-action-btn-delete">
                <DeleteOutlined />
              </button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="campus-management">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">校区管理</h1>
          <p className="admin-page-subtitle">管理所有校区信息，包括校区编码、联系人等</p>
        </div>
        <div className="admin-page-actions">
          <Input
            placeholder="搜索校区..."
            prefix={<SearchOutlined />}
            className="admin-search-input"
            style={{ width: 240 }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            className="admin-btn-primary"
          >
            新建校区
          </Button>
        </div>
      </div>

      {/* 数据卡片 */}
      <Card className="admin-page-card">
        {isMobile ? (
          <Spin spinning={loading}>
            <div>
              {data?.list?.map((record: Campus) => (
                <div key={record.id} style={{ marginBottom: 12 }}>
                  <div className="mobile-card-item">
                    <div className="mobile-card-header">
                      <div>
                        <div className="mobile-card-title">{record.campusName}</div>
                        <div className="mobile-card-meta">
                          <span className="mobile-card-meta-item">编码：{record.campusCode}</span>
                          <span className="mobile-card-meta-item">{record.contactName}</span>
                        </div>
                      </div>
                      <Tag className={`admin-tag ${record.status === 'enabled' ? 'admin-tag-success' : 'admin-tag-error'}`}>
                        {record.status === 'enabled' ? '启用' : '禁用'}
                      </Tag>
                    </div>
                    <div className="mobile-card-actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        style={{ borderRadius: 6 }}
                      >
                        编辑
                      </Button>
                      <Popconfirm
                        title="确定删除该校区吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ borderRadius: 6 }}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Spin>
        ) : (
          <Table
            className="admin-table"
            columns={columns}
            dataSource={data?.list}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize: pageSize || 10 });
              },
            }}
          />
        )}
      </Card>

      {/* Modal */}
      <Modal
        title={
          <div className="modal-title">
            {editingCampus ? '编辑校区' : '新建校区'}
          </div>
        }
        open={modalVisible}
        forceRender
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={isMobile ? '100%' : 560}
        className="admin-modal"
        style={isMobile ? { top: 0, maxWidth: '100vw' } : {}}
        okButtonProps={{ className: 'admin-btn-primary' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="admin-form"
        >
          <Form.Item
            name="campusCode"
            label="校区编码"
            rules={[{ required: true, message: '请输入校区编码' }]}
          >
            <Input 
              disabled={!!editingCampus} 
              placeholder="请输入校区编码" 
              prefix={<HomeOutlined className="input-icon" />}
            />
          </Form.Item>

          <Form.Item
            name="campusName"
            label="校区名称"
            rules={[{ required: true, message: '请输入校区名称' }]}
          >
            <Input placeholder="请输入校区名称" />
          </Form.Item>

          <Form.Item
            name="contactName"
            label="联系人"
            rules={[{ required: true, message: '请输入联系人' }]}
          >
            <Input 
              placeholder="请输入联系人" 
              prefix={<UserOutlined className="input-icon" />}
            />
          </Form.Item>

          <Form.Item
            name="contactPhone"
            label="联系电话"
            rules={[
              { required: true, message: '请输入联系电话' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input 
              placeholder="请输入联系电话" 
              prefix={<PhoneOutlined className="input-icon" />}
            />
          </Form.Item>

          {editingCampus && (
            <Form.Item name="status" label="状态">
              <Select placeholder="请选择状态">
                <Option value="enabled">启用</Option>
                <Option value="disabled">禁用</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="remark" label="备注">
            <Input.TextArea 
              rows={3} 
              placeholder="请输入备注"
              style={{ borderRadius: 10 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CampusManagement;
